<script setup lang="ts">
  // Stock consumption stats migrated to the V2 design system: entity-mode
  // table (one row per stock, unit shown, no totals: mixed units) + bar chart.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import StatsService from '@/api/services/stats.service';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsSeriesReport from '@/components/v2/stats/V2StatsSeriesReport.vue';
  import { IV2StatsTableRow } from '@/components/v2/stats/v2-stats-table.types';
  import { useStatsSeriesDisplay } from '@/composition/use-stats-series-display';
  import { useUi } from '@/composition/use-ui';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import {
    StockMovementType,
    USABLE_STOCK_MOVEMENT_TYPES,
  } from '@/enums/StockMovementType';
  import { IEntityStats } from '@/interfaces/IEntityStats';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  interface IStockEntity {
    name?: string | null;
    unit?: string | null;
  }

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const statsService = new StatsService();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const currencySymbol = computed(() => restaurantStore.currency?.symbol ?? '');

  //#region Filters

  const range = ref<IStatsDateRange | null>(null);

  const MOVEMENT_TYPE_KEYS: Record<number, string> = {
    [StockMovementType.PURCHASE_ORDER]: 'v2Stats.stock.typePurchase',
    [StockMovementType.SALE]: 'v2Stats.stock.typeSale',
    [StockMovementType.STOCK_ADJUSTMENT]: 'v2Stats.stock.typeAdjustment',
  };

  // Computed so a runtime language switch re-labels the chips.
  const movementTypeOptions = computed(() =>
    USABLE_STOCK_MOVEMENT_TYPES.map((type) => ({
      id: type,
      label: i18nT(MOVEMENT_TYPE_KEYS[type]),
    }))
  );

  const selectedMovementTypes = ref<number[]>(
    [...USABLE_STOCK_MOVEMENT_TYPES].filter(
      (type) => type !== StockMovementType.STOCK_ADJUSTMENT
    )
  );

  function toggleMovementType(id: number) {
    const current = selectedMovementTypes.value;
    if (current.includes(id)) {
      if (current.length === 1) return;
      selectedMovementTypes.value = current.filter((t) => t !== id);
    } else {
      selectedMovementTypes.value = [...current, id];
    }
  }

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    load();
  }

  watch(selectedMovementTypes, () => {
    load();
  });

  //#endregion

  //#region Loading

  const statsData = ref<IEntityStats<IStockEntity> | null>(null);
  const loading = ref(false);

  const SERIES_LABEL_KEYS: Record<string, string> = {
    consumption: 'v2Stats.series.consumption',
    additions: 'v2Stats.series.additions',
    cost: 'v2Stats.series.cost',
  };

  const SERIES_COLORS = ['#f56041', '#30d565', '#45a5f5'];

  let requestGeneration = 0;

  async function load() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const stats = await statsService.getStockConsumptionStats(
        restaurant.id,
        branch.id,
        range.value.from,
        range.value.to,
        selectedMovementTypes.value,
        range.value.calendar
      );
      if (generation !== requestGeneration) return;

      stats.labels = stats.groups.length
        ? stats.groups[0].stats.map(
            (stat) =>
              (stat.id != null ? stats.entities[stat.id]?.name : null) ?? ''
          )
        : [];
      statsData.value = stats;
    } catch (error) {
      if (generation !== requestGeneration) return;
      statsData.value = null;
      ui.serverError(error, i18nT('v2Stats.stock.loadError'));
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  const displayStats = useStatsSeriesDisplay({
    stats: statsData,
    labelKeys: SERIES_LABEL_KEYS,
    colors: SERIES_COLORS,
    currencySymbol,
  });

  //#endregion

  function stockUnit(row: IV2StatsTableRow): string | null {
    const entityId = row.entityId as number | undefined;
    if (entityId == null || !statsData.value) return null;
    return statsData.value.entities[entityId]?.unit ?? null;
  }
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.stock.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="stock"
        @change="onRangeChange"
      />
    </template>

    <div class="type-filter">
      <button
        v-for="option in movementTypeOptions"
        :key="option.id"
        type="button"
        class="type-chip"
        :class="{ 'is-active': selectedMovementTypes.includes(option.id) }"
        @click="toggleMovementType(option.id)"
      >
        {{ option.label }}
      </button>
    </div>

    <V2StatsSeriesReport
      :stats="displayStats"
      :loading="loading"
      chart-type="bar"
      :chart-title="String($t('v2Stats.stock.chartTitle'))"
      :chart-limit="15"
      :currency-symbol="currencySymbol"
      export-filename="stock-consumption-stats"
      :export-sheet="String($t('v2Stats.stock.title'))"
      :first-column-label="String($t('v2Stats.colStock'))"
      :default-sort="{ key: 'consumption', desc: true }"
      hide-totals
    >
      <template #first-cell="{ row }">
        <span class="stock-cell">
          <span class="stock-name">{{ String(row.label ?? '') }}</span>
          <span v-if="stockUnit(row)" class="stock-unit"
            >({{ stockUnit(row) }})</span
          >
        </span>
      </template>
    </V2StatsSeriesReport>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .type-filter {
    @apply tw-flex tw-flex-wrap tw-items-center;

    .type-chip {
      // margins instead of flex gap: unsupported on Chrome <84.
      @apply tw-mb-1 tw-mr-1.5 tw-h-11 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

      &.is-active {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-white;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }

  .stock-cell {
    @apply tw-inline-flex tw-min-w-0 tw-flex-wrap tw-items-baseline;

    .stock-name {
      @apply tw-font-medium tw-text-neutral-900;

      // Inside the capped mobile label column: clamp instead of overflowing.
      @media (max-width: 767.98px) {
        @apply tw-min-w-0 tw-overflow-hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        word-break: break-word; // fallback for Chrome <80
        overflow-wrap: anywhere;
      }
    }

    .stock-unit {
      @apply tw-ml-1 tw-shrink-0 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
